import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Typography, FormControlLabel, Checkbox, Modal } from '@material-ui/core';
import { MdWarning } from 'react-icons/md';
import { API } from 'aws-amplify';
import DynamicTable from '../../components/DynamicTable';
import useUIState from '../../hooks/useUIstate';
import { formatDateAndTimeDashboard, formatTimeDashboard } from '../../helpers/commonFunctions';
import { TraceBlockRow } from '../../interfaces/ITraceBlock';

interface FlatTraceRow {
  key: string;
  TraceId: number;
  TraceDate: string;
  Action: 'Insert' | 'Update' | 'Delete';
  ChannelCode: string;
  Enabled: boolean | null;
  ProductId: number | null;
  CurrentStock: number | null;
  UpdatedStock: number | null;
  LatestRefill: string | null;
  Mismatch: boolean;
  IsFirstForChannel: boolean;
  EnabledChanged: boolean;
  ProductIdChanged: boolean;
  CurrentStockChanged: boolean;
  UpdatedStockChanged: boolean;
  BlocksChanged: number | null;
  LogicalName: string;
  HasMessages: boolean;
}

// A row is "meaningful" - worth showing in the condensed view - when it's a channel's first known
// state, an actual value change, or a Delete (a block being removed matters regardless of per-channel diffs).
function isMeaningful(row: FlatTraceRow): boolean {
  return row.IsFirstForChannel
    || row.Action === 'Delete'
    || row.EnabledChanged
    || row.ProductIdChanged
    || row.CurrentStockChanged
    || row.UpdatedStockChanged;
}

function flattenAndAnnotate(rows: TraceBlockRow[]): FlatTraceRow[] {
  const flat: FlatTraceRow[] = [];

  rows.forEach(row => {
    if (row.Channels.length === 0) {
      flat.push({
        key: `${row.TraceId}-empty`,
        TraceId: row.TraceId,
        TraceDate: row.TraceDate,
        Action: row.Action,
        ChannelCode: '—',
        Enabled: null,
        ProductId: null,
        CurrentStock: null,
        UpdatedStock: null,
        LatestRefill: null,
        Mismatch: false,
        IsFirstForChannel: true,
        EnabledChanged: false,
        ProductIdChanged: false,
        CurrentStockChanged: false,
        UpdatedStockChanged: false,
        BlocksChanged: row.BlocksChanged,
        LogicalName: row.LogicalName,
        HasMessages: false,
      });
      return;
    }

    row.Channels.forEach(channel => {
      flat.push({
        key: `${row.TraceId}-${channel.Code}`,
        TraceId: row.TraceId,
        TraceDate: row.TraceDate,
        Action: row.Action,
        ChannelCode: channel.Code,
        Enabled: channel.Enabled,
        ProductId: channel.ProductId,
        CurrentStock: channel.CurrentStock,
        UpdatedStock: channel.UpdatedStock,
        LatestRefill: channel.LatestRefill,
        Mismatch: channel.CurrentStock !== null && channel.UpdatedStock !== null && channel.CurrentStock !== channel.UpdatedStock,
        IsFirstForChannel: false,
        EnabledChanged: false,
        ProductIdChanged: false,
        CurrentStockChanged: false,
        UpdatedStockChanged: false,
        BlocksChanged: row.BlocksChanged,
        LogicalName: row.LogicalName,
        HasMessages: row.Action !== 'Delete',
      });
    });
  });

  // Sort by channel code, then chronologically - each channel's rows become a contiguous timeline.
  flat.sort((a, b) => {
    if (a.ChannelCode !== b.ChannelCode)
      return a.ChannelCode.localeCompare(b.ChannelCode);
    return new Date(a.TraceDate).getTime() - new Date(b.TraceDate).getTime();
  });

  // Flag the first entry per channel and cells that changed since that channel's previous (non-delete) entry.
  const previousByChannel = new Map<string, FlatTraceRow>();
  flat.forEach(entry => {
    if (entry.Action === 'Delete')
      return;

    const previous = previousByChannel.get(entry.ChannelCode);
    if (previous) {
      entry.EnabledChanged = previous.Enabled !== entry.Enabled;
      entry.ProductIdChanged = previous.ProductId !== entry.ProductId;
      entry.CurrentStockChanged = previous.CurrentStock !== entry.CurrentStock;
      entry.UpdatedStockChanged = previous.UpdatedStock !== entry.UpdatedStock;
    } else {
      entry.IsFirstForChannel = true;
    }
    previousByChannel.set(entry.ChannelCode, entry);
  });

  return flat;
}

function HighlightableValue({ value, changed }: { value: number | null; changed: boolean }) {
  if (value === null || value === undefined)
    return <>{'—'}</>;
  if (!changed)
    return <>{value}</>;
  return (
    <span style={{ fontWeight: 700, backgroundColor: '#E3F2FD', padding: '2px 6px', borderRadius: '4px' }}>
      {value}
    </span>
  );
}

// JofemarDispenseMessage.Timestamp is UTC but serialized without a timezone suffix (same convention as
// TraceDate), so a bare `new Date(...)` would misinterpret it as browser-local time. Appending 'Z' makes
// the Date parser treat it as UTC, which then renders correctly in the browser's local timezone.
function getUtcTime(timestamp: string | null | undefined): number {
  return timestamp ? new Date(`${timestamp}Z`).getTime() : NaN;
}

function ActionChip({ action }: { action: 'Insert' | 'Update' | 'Delete' }) {
  const colors: Record<string, { color: string; borderColor: string }> = {
    Insert: { color: '#2e7d32', borderColor: '#2e7d32' },
    Update: { color: '#455a64', borderColor: '#455a64' },
    Delete: { color: '#c62828', borderColor: '#c62828' },
  };
  const style = colors[action] || colors.Update;
  return <Chip label={action} variant="outlined" size="small" style={style} />;
}

interface TraceBlocksTableProps {
  rows: TraceBlockRow[];
  storeId: number;
}

function TraceBlocksTable({ rows, storeId }: TraceBlocksTableProps) {
  const { t } = useTranslation();
  const { setToastMessage, setShowToastMessage, setToastType } = useUIState();
  const [showAllRows, setShowAllRows] = useState(false);
  const [dispenseModalOpen, setDispenseModalOpen] = useState(false);
  const [dispenseModalLines, setDispenseModalLines] = useState<string[]>([]);
  const flatRows = React.useMemo(() => flattenAndAnnotate(rows), [rows]);
  const visibleRows = React.useMemo(
    () => (showAllRows ? flatRows : flatRows.filter(isMeaningful)),
    [flatRows, showAllRows]
  );

  function openDispenseMessages(row: any) {
    setDispenseModalLines([]);
    setDispenseModalOpen(true);
    API.get('AE', '/TraceBlocks/DispenseMessages', {
      response: true,
      queryStringParameters: {
        storeId,
        vendingMachine: row.values.LogicalName,
        channel: row.values.ChannelCode,
        beforeDate: row.values.TraceDate,
      },
    })
      .then(res => {
        if (!Array.isArray(res.data) || res.data.length === 0) {
          setDispenseModalLines([t('traceblocks.no-dispense-messages') as string]);
          return;
        }
        const lines = res.data
          .sort((a: any, b: any) => getUtcTime(a.Timestamp) - getUtcTime(b.Timestamp))
          .map((item: any) => `${item.Timestamp ? formatTimeDashboard(`${item.Timestamp}Z`) : '—'}  ${item.DispensationStatus}`);
        setDispenseModalLines(lines);
      })
      .catch(error => {
        console.log(error);
        setToastType('error');
        setToastMessage(t('traceblocks.error.getting-dispensemessages') as string);
        setShowToastMessage(true);
        setDispenseModalLines([t('traceblocks.error.getting-dispensemessages') as string]);
      });
  }

  const columns = React.useMemo(
    () => [
      {
        Header: t('traceblocks.column.trace-date'),
        accessor: 'TraceDate',
        Cell: ({ value }: any) => formatDateAndTimeDashboard(value),
      },
      {
        Header: t('traceblocks.column.action'),
        accessor: 'Action',
        Cell: ({ value }: any) => <ActionChip action={value} />,
      },
      {
        Header: t('traceblocks.column.channel'),
        accessor: 'ChannelCode',
      },
      {
        Header: t('traceblocks.column.enabled'),
        accessor: 'Enabled',
        Cell: ({ row }: any) => {
          const value = row.original.Enabled;
          const label = value === null ? '—' : value ? t('generic.yes') : t('generic.no');
          return row.original.EnabledChanged ? (
            <span style={{ fontWeight: 700, backgroundColor: '#E3F2FD', padding: '2px 6px', borderRadius: '4px' }}>
              {label}
            </span>
          ) : <>{label}</>;
        },
      },
      {
        Header: t('traceblocks.column.product-id'),
        accessor: 'ProductId',
        Cell: ({ row }: any) => (
          <HighlightableValue value={row.original.ProductId} changed={row.original.ProductIdChanged} />
        ),
      },
      {
        Header: t('traceblocks.column.current-stock'),
        accessor: 'CurrentStock',
        Cell: ({ row }: any) => (
          <HighlightableValue value={row.original.CurrentStock} changed={row.original.CurrentStockChanged} />
        ),
      },
      {
        Header: t('traceblocks.column.updated-stock'),
        accessor: 'UpdatedStock',
        Cell: ({ row }: any) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <HighlightableValue value={row.original.UpdatedStock} changed={row.original.UpdatedStockChanged} />
            {row.original.Mismatch ? <MdWarning color="#f57c00" title={t('traceblocks.legend') as string} /> : null}
          </div>
        ),
      },
      {
        Header: t('traceblocks.column.blocks-changed'),
        accessor: 'BlocksChanged',
        Cell: ({ value }: any) => (value === null || value === undefined ? '—' : value),
      },
      {
        Header: t('traceblocks.column.latest-refill'),
        accessor: 'LatestRefill',
        Cell: ({ value }: any) => (value ? formatDateAndTimeDashboard(value) : '—'),
      },
      {
        Header: 'HasMessages',
        accessor: 'HasMessages',
        hide: true,
      },
      {
        Header: 'LogicalName',
        accessor: 'LogicalName',
        hide: true,
      },
    ],
    [t]
  );

  function getRowStyle(row: any): React.CSSProperties {
    const original: FlatTraceRow = row.original;
    const style: React.CSSProperties = {};
    if (original.Mismatch) {
      style.backgroundColor = '#FFF3E0';
    }
    if (original.Action === 'Delete') {
      style.textDecoration = 'line-through';
      style.color = 'rgba(0, 0, 0, 0.4)';
    }
    return style;
  }

  return (
    <div>
      <Typography variant="caption" component="p" style={{ marginBottom: '8px' }}>
        {t('traceblocks.legend')}
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={showAllRows}
            onChange={e => setShowAllRows(e.target.checked)}
          />
        }
        label={t('traceblocks.show-all-rows') as string}
      />
      <DynamicTable
        columns={columns}
        data={visibleRows}
        loading={false}
        globalFilter
        getRowStyle={getRowStyle}
        showMessages={openDispenseMessages}
      />
      <Modal open={dispenseModalOpen} onClose={() => setDispenseModalOpen(false)}>
        <div style={{ background: '#fff', padding: 20, margin: '10% auto', maxWidth: 400 }}>
          <h3>{t('traceblocks.dispense-messages-title')}</h3>
          {dispenseModalLines.map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
          <button style={{ marginTop: '16px' }} onClick={() => setDispenseModalOpen(false)}>{t('generic.close')}</button>
        </div>
      </Modal>
    </div>
  );
}

export default TraceBlocksTable;
